import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { PlatformSyncStatus } from 'src/shared/services/listing-optimization-settings.service';

/**
 * One merged calendar cell from `GET /listing-calendar` (gotidy #8271).
 * `price` is the EFFECTIVE nightly price in cents (override → PriceLabs → base,
 * resolved server-side); `minStay` the effective min nights. Both may be null
 * when nothing is stored for that date yet.
 */
export interface ListingCalendarDay {
  date: string;               // YYYY-MM-DD
  price: number | null;       // cents
  currency: string;
  minStay: number | null;     // nights
  isBlocked: boolean;         // customer blocked the date
  isBooked: boolean;          // a guest reservation occupies the night
}

/** Platform slugs the discount levers sync to, in the order the API returns them. */
export type DiscountPlatform = 'airbnb' | 'vrbo' | 'booking_com';

/**
 * How a platform's discount for one date is doing. `configured` means no
 * discount applies to the date, so there is nothing to reconcile; `pending`
 * and `failed` both retry, so neither is final.
 */
export type PlatformDiscountStatus = 'synced' | 'pending' | 'failed' | 'configured';

/**
 * One connected platform's discount for a single date. Only the two
 * booking-window levers (`far_out`, `last_minute`) can produce a per-date
 * discount — the other five are stay-length or guest-conditional and have no
 * deterministic per-date value, so they never appear here even when enabled.
 */
export interface PlatformDiscount {
  platform: DiscountPlatform;
  discountPct: number;
  /** Cents, already discounted off the effective price; null when the date has no price. */
  finalPrice: number | null;
  /** The lever that won for this platform, or '' when no discount applies. */
  reason: 'far_out' | 'last_minute' | '';
  status: PlatformDiscountStatus;
}

/** Day detail from `GET /listing-calendar/:date` — the month cell plus analytics/override, all flat. */
export interface ListingCalendarDayDetail extends ListingCalendarDay {
  suggestedPrice: number | null;  // PriceLabs recommendation snapshot, cents
  overridePrice: number | null;   // customer's stored manual override, cents
  minPrice: number | null;        // per-date override bounds, cents
  maxPrice: number | null;
  /**
   * One row per CONNECTED platform, airbnb → vrbo → booking_com. Platforms with
   * no connected listing are absent, so this can legitimately be empty.
   */
  platformDiscounts: PlatformDiscount[];
}

/**
 * Per-date settings write. Semantics per field: a value SETS it, `null` CLEARS
 * it, leaving the key `undefined` (absent) leaves it UNCHANGED on the backend.
 * Prices are cents and must be whole dollars (multiples of 100).
 */
export interface DateSettingsWrite {
  overridePrice?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  minStay?: number | null;
  isBlocked?: boolean | null;
  /** Why the dates are blocked. Only saved together with `isBlocked: true`; unblocking clears it. */
  blockReason?: string | null;
}

/** A stored per-date override row from `GET /address-settings-by-date` (only dates that have one). */
export interface DateSettings {
  date: string;
  overridePrice: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  minStay: number | null;
  isBlocked: boolean;
  /** The note left when the date was blocked; null when none or not blocked. */
  blockReason: string | null;
  /** True only when every listed target of every listed key is confirmed. */
  synced: boolean | null;
  /**
   * Grouped by setting key, then by target, with each target's sync status
   * (`synced` | `pending` | `failed`). Targets are the connected platforms plus a
   * synthetic `tidy_pricing` entry on `override_price`, `min_price` and `max_price`,
   * which route through TIDY's pricing API. Keys with no
   * target for this address are omitted.
   */
  platformSync: Record<string, Record<string, PlatformSyncStatus>>;
}

interface CalendarDayResponse {
  date: string;
  price: number | null;
  currency: string;
  min_stay: number | null;
  is_blocked: boolean;
  is_booked: boolean;
}

interface PlatformDiscountResponse {
  platform: DiscountPlatform;
  discount_pct: number | null;
  final_price: number | null;
  reason: string | null;
  status: PlatformDiscountStatus | null;
}

interface DayDetailResponse extends CalendarDayResponse {
  suggested_price: number | null;
  override_price: number | null;
  min_price: number | null;
  max_price: number | null;
  platform_discounts?: PlatformDiscountResponse[] | null;
}

interface DateSettingsResponse {
  date: string;
  override_price: number | null;
  min_price: number | null;
  max_price: number | null;
  min_stay: number | null;
  is_blocked: boolean | null;
  block_reason?: string | null;
  synced?: boolean | null;
  platform_sync?: Record<string, Record<string, PlatformSyncStatus>> | null;
}

/**
 * Transport for the Optimizer pricing calendar (gotidy #8271): the read
 * calendar (`listing-calendar`) and the per-date settings resource
 * (`address-settings-by-date`). Replaces the removed `address-pricing`
 * calendar/customization endpoints. Account-default price bounds are NOT
 * here — they ride the generic address-settings store under the `optimizer`
 * category (see ListingOptimizationSettingsService).
 */
@Injectable({ providedIn: 'root' })
export class ListingCalendarService {

  private readonly http = inject(HttpClient);
  private readonly base = `${environment.api_url}api/v1/customer`;

  // In-flight per-date writes. A refetch (e.g. the calendar reloading when the
  // day-detail panel closes) awaits these first so it never races ahead of a
  // block/override POST and repaints the pre-write state.
  private readonly pendingWrites = new Set<Promise<unknown>>();

  /** Month calendar — one merged cell per date over the window (default today → +30d). */
  async getMonth(addressId: number, dateFrom?: string, dateTo?: string): Promise<ListingCalendarDay[]> {
    const params: Record<string, string | number> = { address_id: addressId };
    if (dateFrom) { params['date_from'] = dateFrom; }
    if (dateTo) { params['date_to'] = dateTo; }
    const response = await firstValueFrom(
      this.http.get<CalendarDayResponse[]>(`${this.base}/listing-calendar`, { params })
    );
    return (response ?? []).map(day => this.mapDay(day));
  }

  /** Per-day detail: the month cell plus suggested/override price and per-date bounds. */
  async getDay(addressId: number, date: string): Promise<ListingCalendarDayDetail> {
    const response = await firstValueFrom(
      this.http.get<DayDetailResponse>(`${this.base}/listing-calendar/${date}`, {
        params: { address_id: addressId },
      })
    );
    return {
      ...this.mapDay(response),
      suggestedPrice: response.suggested_price ?? null,
      overridePrice: response.override_price ?? null,
      minPrice: response.min_price ?? null,
      maxPrice: response.max_price ?? null,
      platformDiscounts: (response.platform_discounts ?? []).map(row => ({
        platform: row.platform,
        discountPct: row.discount_pct ?? 0,
        finalPrice: row.final_price ?? null,
        reason: (row.reason === 'far_out' || row.reason === 'last_minute') ? row.reason : '',
        status: row.status ?? 'configured',
      })),
    };
  }

  /**
   * Applies `settings` to every date in [startDate, endDate] (single day →
   * start === end). Undefined fields are omitted from the payload so the
   * backend leaves them unchanged; null is sent through to clear.
   */
  async setDateSettings(
    addressId: number,
    startDate: string,
    endDate: string,
    settings: DateSettingsWrite
  ): Promise<void> {
    const payload: Record<string, unknown> = {};
    if (settings.overridePrice !== undefined) { payload['override_price'] = settings.overridePrice; }
    if (settings.minPrice !== undefined) { payload['min_price'] = settings.minPrice; }
    if (settings.maxPrice !== undefined) { payload['max_price'] = settings.maxPrice; }
    if (settings.minStay !== undefined) { payload['min_stay'] = settings.minStay; }
    if (settings.isBlocked !== undefined) { payload['is_blocked'] = settings.isBlocked; }
    if (settings.blockReason !== undefined) { payload['block_reason'] = settings.blockReason; }
    const request = firstValueFrom(
      this.http.post(`${this.base}/address-settings-by-date`, {
        address_id: addressId,
        start_date: startDate,
        end_date: endDate,
        settings: payload,
      })
    );
    this.pendingWrites.add(request);
    try {
      await request;
    } finally {
      this.pendingWrites.delete(request);
    }
  }

  /** Resolves once every in-flight per-date write has settled (success or error). */
  async whenWritesSettled(): Promise<void> {
    await Promise.allSettled([...this.pendingWrites]);
  }

  /** Stored per-date overrides/blocks — one entry only for dates that have one (default window today → +12mo). */
  async getDateSettings(addressId: number, dateFrom?: string, dateTo?: string): Promise<DateSettings[]> {
    const params: Record<string, string | number> = { address_id: addressId };
    if (dateFrom) { params['date_from'] = dateFrom; }
    if (dateTo) { params['date_to'] = dateTo; }
    const response = await firstValueFrom(
      this.http.get<DateSettingsResponse[]>(`${this.base}/address-settings-by-date`, { params })
    );
    return (response ?? []).map(row => ({
      date: row.date,
      overridePrice: row.override_price ?? null,
      minPrice: row.min_price ?? null,
      maxPrice: row.max_price ?? null,
      minStay: row.min_stay ?? null,
      isBlocked: row.is_blocked === true,
      blockReason: row.block_reason ?? null,
      synced: row.synced ?? null,
      platformSync: row.platform_sync ?? {},
    }));
  }

  /**
   * Clears ALL per-date settings for one date (override fields + block).
   *
   * Deliberately a POST of explicit nulls, not a DELETE. The DELETE endpoint
   * only accepts keys with no external sync destination, and every per-date key
   * we write syncs somewhere — so it rejects all of them. A null through this
   * path is recorded as a tombstone, which pushes the removal out to PriceLabs
   * and the channels and then confirms it; a hard delete would leave the old
   * value sitting on the platform.
   */
  async clearDateSettings(addressId: number, date: string): Promise<void> {
    await this.setDateSettings(addressId, date, date, {
      overridePrice: null,
      minPrice: null,
      maxPrice: null,
      minStay: null,
      isBlocked: null,
    });
  }

  private mapDay(day: CalendarDayResponse): ListingCalendarDay {
    return {
      date: day.date,
      price: day.price ?? null,
      currency: day.currency,
      minStay: day.min_stay ?? null,
      isBlocked: day.is_blocked === true,
      isBooked: day.is_booked === true,
    };
  }
}
